/**
 * Constructor — editor no-code de secciones/subsecciones del dashboard.
 * Carlos crea secciones propias con bloques (notas, listas, enlaces, KPIs),
 * las organiza y también puede ocultar/renombrar/reordenar las existentes.
 * Config se guarda en el servidor vía ctx.saveCfg (ruta /api/v1/ui-config).
 *
 * Exports:
 *   renderConstructor(root, ctx)           -> editor
 *   renderCustomSection(root, ctx, section) -> vista de una sección propia
 */

// ---------- helpers DOM (self-contained) ----------
function h(tag, attrs = {}, children = []) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs || {})) {
    if (v == null || v === false) continue;
    if (k === 'class') el.className = v;
    else if (k === 'style') el.style.cssText = v;
    else if (k === 'html') el.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v === true) el.setAttribute(k, '');
    else el.setAttribute(k, v);
  }
  for (const c of [].concat(children)) {
    if (c == null || c === false) continue;
    el.appendChild(typeof c === 'string' || typeof c === 'number' ? document.createTextNode(String(c)) : c);
  }
  return el;
}
function uid(p) { return p + '_' + Math.random().toString(36).slice(2, 9); }
function clone(o) { return JSON.parse(JSON.stringify(o)); }

const EMOJIS = ['🧩','📊','🚗','🏠','🏢','💼','📈','📝','✅','💸','📣','🎬','🍷','🛠️','⭐','🔧','📦','🧠','🌍','💡','🔔','🎯','🗂️','📅','🏦','🧾','🤝','🚀','🛒','📷'];

function normCfg(cfg) {
  cfg = cfg || {};
  cfg.version = cfg.version || 1;
  if (!Array.isArray(cfg.customSections)) cfg.customSections = [];
  if (!cfg.navOverrides || typeof cfg.navOverrides !== 'object') cfg.navOverrides = {};
  return cfg;
}
function normSection(s) {
  s.id = s.id || uid('cs');
  s.label = s.label || 'Sección';
  s.emoji = s.emoji || '🧩';
  if (!Array.isArray(s.tabs) || s.tabs.length === 0) s.tabs = [{ id: uid('tab'), label: 'General', blocks: [] }];
  s.tabs.forEach((t) => { t.id = t.id || uid('tab'); t.label = t.label || 'Pestaña'; if (!Array.isArray(t.blocks)) t.blocks = []; });
  return s;
}

const btn = (label, extra, on) => h('button', {
  class: 'px-3 py-1.5 rounded-lg text-sm font-medium transition ' + (extra || 'bg-base-700 text-ink-100 hover:bg-base-600'),
  onclick: on,
}, label);
const iconBtn = (label, on, title) => h('button', {
  class: 'px-2 py-1 rounded-md text-sm bg-base-700 text-ink-200 hover:bg-base-600 transition', title: title || '',
  onclick: on,
}, label);
const input = (val, ph, on, cls) => {
  const el = h('input', { type: 'text', value: val || '', placeholder: ph || '',
    class: (cls || 'w-full') + ' bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-ink-100 focus:border-gold-400 focus:outline-none transition-colors' });
  el.addEventListener('change', () => on(el.value));
  return el;
};

// ============================================================
// EDITOR
// ============================================================
export function renderConstructor(root, ctx) {
  const cfg = normCfg(clone(ctx.cfg || {}));
  cfg.customSections.forEach(normSection);

  // ¿venimos de "Editar" una sección concreta?
  let editingId = window.__openBuilderSection || null;
  window.__openBuilderSection = null;
  if (editingId && !cfg.customSections.some((s) => s.id === editingId)) editingId = null;
  let activeTab = {};

  async function save(reloadNav) {
    await ctx.saveCfg(clone(cfg));
    flash();
    if (reloadNav && ctx.reloadUI) {
      // refresca el menú pero seguimos en el constructor
      window.__openBuilderSection = editingId;
      await ctx.reloadUI();
      return;
    }
    draw();
  }
  function flash() {
    const f = root.querySelector('[data-flash]');
    if (f) { f.textContent = 'Guardado ✓'; f.style.opacity = '1'; setTimeout(() => { f.style.opacity = '0'; }, 1300); }
  }

  function draw() {
    root.innerHTML = '';
    root.appendChild(header());
    root.appendChild(editingId ? sectionEditor(cfg.customSections.find((s) => s.id === editingId)) : listView());
  }

  function header() {
    return h('div', { class: 'flex items-center justify-between gap-3 mb-6 flex-wrap' }, [
      h('div', {}, [
        h('h2', { class: 'font-display text-3xl font-bold' }, [h('span', {}, '🛠️ '), 'Constructor']),
        h('p', { class: 'text-ink-300 text-sm mt-1' }, 'Crea y organiza tus propias secciones sin tocar código.'),
      ]),
      h('div', { class: 'flex items-center gap-2' }, [
        h('span', { 'data-flash': '1', class: 'text-emerald-300 text-sm', style: 'opacity:0; transition:opacity .3s;' }, 'Guardado ✓'),
        editingId ? btn('← Volver', 'bg-base-700 text-ink-100 hover:bg-base-600', () => { editingId = null; draw(); }) : null,
        ctx.reloadUI ? iconBtn('↻ Menú', async () => { window.__openBuilderSection = editingId; await ctx.reloadUI(); }, 'Actualizar el menú superior') : null,
      ]),
    ]);
  }

  // ---------- Vista lista ----------
  function listView() {
    const wrap = h('div', { class: 'space-y-6' });

    // Panel 1: Mis secciones
    const mis = h('div', { class: 'bg-base-800 rounded-2xl p-5' }, [
      h('div', { class: 'flex items-center justify-between mb-4' }, [
        h('h3', { class: 'font-display text-lg font-bold text-ink-100' }, 'Mis secciones'),
        btn('➕ Nueva sección', 'bg-gold-400 text-base-900 hover:bg-gold-300 font-semibold', () => {
          const name = (prompt('Nombre de la nueva sección:') || '').trim();
          if (!name) return;
          const s = normSection({ id: uid('cs'), label: name, emoji: '🧩', order: cfg.customSections.length, tabs: [] });
          cfg.customSections.push(s);
          editingId = s.id;
          save(true);
        }),
      ]),
      cfg.customSections.length === 0
        ? h('p', { class: 'text-ink-400 text-sm' }, 'Aún no tienes secciones propias. Pulsa “Nueva sección” para empezar.')
        : h('div', { class: 'space-y-2' }, cfg.customSections.map((s, i) => sectionRow(s, i))),
    ]);

    // Panel 2: Organizar menú existente
    const org = h('div', { class: 'bg-base-800 rounded-2xl p-5' }, [
      h('h3', { class: 'font-display text-lg font-bold text-ink-100 mb-1' }, 'Organizar el menú'),
      h('p', { class: 'text-ink-400 text-xs mb-4' }, 'Oculta, renombra o reordena las secciones que ya existen. (No se borran, solo se esconden.)'),
      h('div', { class: 'space-y-4' }, (ctx.builtin || []).map((it) => builtinRow(it))),
    ]);

    wrap.appendChild(mis);
    wrap.appendChild(org);
    return wrap;
  }

  function sectionRow(s, i) {
    return h('div', { class: 'flex items-center gap-2 bg-base-900 border border-base-700 rounded-xl px-3 py-2' }, [
      h('span', { class: 'text-xl' }, s.emoji),
      h('span', { class: 'flex-1 text-ink-100 font-medium truncate' }, s.label),
      s.hidden ? h('span', { class: 'text-[10px] text-ink-400 border border-base-600 rounded px-1.5 py-0.5' }, 'oculta') : null,
      iconBtn('✏️', () => { editingId = s.id; draw(); }, 'Editar contenido'),
      iconBtn('↑', () => { if (i > 0) { const a = cfg.customSections; [a[i-1], a[i]] = [a[i], a[i-1]]; a.forEach((x,k)=>x.order=k); save(true); } }, 'Subir'),
      iconBtn('↓', () => { const a = cfg.customSections; if (i < a.length-1) { [a[i+1], a[i]] = [a[i], a[i+1]]; a.forEach((x,k)=>x.order=k); save(true); } }, 'Bajar'),
      iconBtn(s.hidden ? '👁️' : '🙈', () => { s.hidden = !s.hidden; save(true); }, s.hidden ? 'Mostrar' : 'Ocultar'),
      iconBtn('🗑️', () => { if (confirm('¿Borrar la sección “' + s.label + '” y todo su contenido?')) { cfg.customSections = cfg.customSections.filter((x) => x.id !== s.id); save(true); } }, 'Borrar'),
    ]);
  }

  function builtinRow(it) {
    const ov = cfg.navOverrides;
    const line = (node) => {
      const o = ov[node.id] || {};
      const row = h('div', { class: 'flex items-center gap-2 py-1' }, [
        h('span', { class: 'text-lg w-6 text-center' }, node.emoji || '•'),
        input(o.label || node.label, node.label, (v) => { ov[node.id] = Object.assign({}, ov[node.id], { label: v.trim() || undefined }); save(false); }, 'flex-1'),
        iconBtn(o.hidden ? '👁️' : '🙈', () => { ov[node.id] = Object.assign({}, ov[node.id], { hidden: !o.hidden }); save(true); }, o.hidden ? 'Mostrar' : 'Ocultar'),
      ]);
      return row;
    };
    if (it.group) {
      return h('div', { class: 'border border-base-700 rounded-xl p-3' }, [
        h('div', { class: 'text-[11px] font-bold uppercase tracking-wider text-ink-400 mb-2' }, (it.emoji || '') + ' ' + it.label),
        h('div', { class: 'space-y-1' }, (it.children || []).map(line)),
      ]);
    }
    return h('div', { class: 'border border-base-700 rounded-xl p-2' }, [line(it)]);
  }

  // ---------- Editor de una sección ----------
  function sectionEditor(s) {
    normSection(s);
    if (!activeTab[s.id] || !s.tabs.some((t) => t.id === activeTab[s.id])) activeTab[s.id] = s.tabs[0].id;
    const tab = s.tabs.find((t) => t.id === activeTab[s.id]);

    const head = h('div', { class: 'bg-base-800 rounded-2xl p-5 mb-4' }, [
      h('div', { class: 'flex items-center gap-3 flex-wrap' }, [
        h('span', { class: 'text-2xl' }, s.emoji),
        input(s.label, 'Nombre de la sección', (v) => { s.label = v.trim() || s.label; save(true); }, 'flex-1 min-w-[180px]'),
        btn('👁️ Ver', 'bg-base-700 text-ink-100 hover:bg-base-600', () => { ctx.navigate && ctx.navigate(s.id); }),
      ]),
      h('div', { class: 'mt-3' }, [
        h('div', { class: 'text-xs text-ink-400 mb-1' }, 'Emoji'),
        h('div', { class: 'flex flex-wrap gap-1' }, EMOJIS.map((e) => h('button', {
          class: 'w-8 h-8 rounded-lg text-lg transition ' + (s.emoji === e ? 'bg-gold-400/20 border border-gold-400/40' : 'bg-base-900 border border-base-700 hover:bg-base-700'),
          onclick: () => { s.emoji = e; save(true); },
        }, e))),
      ]),
    ]);

    // Pestañas (subsecciones)
    const tabsBar = h('div', { class: 'flex items-center gap-1 flex-wrap mb-4' }, [
      ...s.tabs.map((t) => h('button', {
        class: 'px-3 py-1.5 rounded-lg text-sm font-medium transition flex items-center gap-1 ' + (t.id === tab.id ? 'bg-gold-400/10 text-gold-300 border border-gold-400/30' : 'text-ink-200 bg-base-800 hover:bg-base-700 border border-transparent'),
        onclick: () => { activeTab[s.id] = t.id; draw(); },
      }, [
        h('span', {}, t.label),
        t.id === tab.id ? h('span', { class: 'text-xs opacity-60 ml-1', title: 'Renombrar',
          onclick: (e) => { e.stopPropagation(); const n = (prompt('Nombre de la subsección:', t.label) || '').trim(); if (n) { t.label = n; save(false); } } }, '✏️') : null,
      ])),
      iconBtn('➕ Subsección', () => { const n = (prompt('Nombre de la nueva subsección:') || '').trim(); if (!n) return; const nt = { id: uid('tab'), label: n, blocks: [] }; s.tabs.push(nt); activeTab[s.id] = nt.id; save(false); }),
      s.tabs.length > 1 ? iconBtn('🗑️ Subsección', () => { if (confirm('¿Borrar la subsección “' + tab.label + '”?')) { s.tabs = s.tabs.filter((x) => x.id !== tab.id); activeTab[s.id] = s.tabs[0].id; save(false); } }) : null,
    ]);

    // Bloques del tab activo
    const blocksWrap = h('div', { class: 'space-y-3' }, tab.blocks.length === 0
      ? [h('p', { class: 'text-ink-400 text-sm bg-base-800 rounded-2xl p-5' }, 'Sin bloques todavía. Añade uno abajo.')]
      : tab.blocks.map((b, bi) => blockEditor(s, tab, b, bi)));

    const addBar = h('div', { class: 'flex flex-wrap gap-2 mt-4' }, [
      btn('➕ Nota', 'bg-base-700 hover:bg-base-600 text-ink-100', () => addBlock(tab, { id: uid('b'), type: 'note', text: '' })),
      btn('➕ Lista', 'bg-base-700 hover:bg-base-600 text-ink-100', () => addBlock(tab, { id: uid('b'), type: 'checklist', title: '', items: [] })),
      btn('➕ Enlaces', 'bg-base-700 hover:bg-base-600 text-ink-100', () => addBlock(tab, { id: uid('b'), type: 'links', title: '', links: [] })),
      btn('➕ KPIs', 'bg-base-700 hover:bg-base-600 text-ink-100', () => addBlock(tab, { id: uid('b'), type: 'kpi', title: '', items: [] })),
    ]);

    return h('div', {}, [head, tabsBar, blocksWrap, addBar]);

    function addBlock(t, blk) { t.blocks.push(blk); save(false); }
  }

  function blockHeader(s, tab, b, bi, title) {
    return h('div', { class: 'flex items-center gap-2 mb-2' }, [
      h('span', { class: 'text-xs font-bold uppercase tracking-wider text-gold-300' }, title),
      h('div', { class: 'flex-1' }),
      iconBtn('↑', () => { if (bi > 0) { [tab.blocks[bi-1], tab.blocks[bi]] = [tab.blocks[bi], tab.blocks[bi-1]]; save(false); } }, 'Subir'),
      iconBtn('↓', () => { if (bi < tab.blocks.length-1) { [tab.blocks[bi+1], tab.blocks[bi]] = [tab.blocks[bi], tab.blocks[bi+1]]; save(false); } }, 'Bajar'),
      iconBtn('🗑️', () => { tab.blocks = tab.blocks.filter((x) => x.id !== b.id); save(false); }, 'Borrar bloque'),
    ]);
  }

  function blockEditor(s, tab, b, bi) {
    const card = h('div', { class: 'bg-base-800 rounded-2xl p-4' });
    if (b.type === 'note') {
      card.appendChild(blockHeader(s, tab, b, bi, '📝 Nota'));
      const ta = h('textarea', { rows: '4', placeholder: 'Escribe aquí…',
        class: 'w-full bg-base-900 border border-base-600 rounded-lg px-3 py-2 text-ink-100 focus:border-gold-400 focus:outline-none' }, b.text || '');
      ta.value = b.text || '';
      ta.addEventListener('change', () => { b.text = ta.value; save(false); });
      card.appendChild(ta);
    } else if (b.type === 'checklist') {
      card.appendChild(blockHeader(s, tab, b, bi, '✅ Lista'));
      card.appendChild(input(b.title, 'Título (opcional)', (v) => { b.title = v; save(false); }, 'w-full mb-2'));
      if (!Array.isArray(b.items)) b.items = [];
      card.appendChild(h('div', { class: 'space-y-1' }, b.items.map((it, idx) => h('div', { class: 'flex items-center gap-2' }, [
        h('input', { type: 'checkbox', checked: !!it.done, class: 'w-5 h-5 accent-blue-400',
          onchange: (e) => { it.done = e.target.checked; save(false); } }),
        input(it.t, 'Tarea…', (v) => { it.t = v; save(false); }, 'flex-1'),
        iconBtn('✕', () => { b.items = b.items.filter((x) => x !== it); save(false); }, 'Quitar'),
      ]))));
      card.appendChild(h('div', { class: 'mt-2' }, [iconBtn('➕ Ítem', () => { b.items.push({ t: '', done: false }); save(false); })]));
    } else if (b.type === 'links') {
      card.appendChild(blockHeader(s, tab, b, bi, '🔗 Enlaces'));
      card.appendChild(input(b.title, 'Título (opcional)', (v) => { b.title = v; save(false); }, 'w-full mb-2'));
      if (!Array.isArray(b.links)) b.links = [];
      card.appendChild(h('div', { class: 'space-y-2' }, b.links.map((lk) => h('div', { class: 'flex items-center gap-2' }, [
        input(lk.emoji, '🔗', (v) => { lk.emoji = v; save(false); }, 'w-14 text-center'),
        input(lk.label, 'Nombre', (v) => { lk.label = v; save(false); }, 'flex-1'),
        input(lk.url, 'https://…', (v) => { lk.url = v; save(false); }, 'flex-1'),
        iconBtn('✕', () => { b.links = b.links.filter((x) => x !== lk); save(false); }, 'Quitar'),
      ]))));
      card.appendChild(h('div', { class: 'mt-2' }, [iconBtn('➕ Enlace', () => { b.links.push({ emoji: '🔗', label: '', url: '' }); save(false); })]));
    } else if (b.type === 'kpi') {
      card.appendChild(blockHeader(s, tab, b, bi, '📊 KPIs'));
      card.appendChild(input(b.title, 'Título (opcional)', (v) => { b.title = v; save(false); }, 'w-full mb-2'));
      if (!Array.isArray(b.items)) b.items = [];
      card.appendChild(h('div', { class: 'space-y-2' }, b.items.map((it) => h('div', { class: 'flex items-center gap-2' }, [
        input(it.label, 'Etiqueta (ej. Ingresos)', (v) => { it.label = v; save(false); }, 'flex-1'),
        input(it.value, 'Valor (ej. 24.800 €)', (v) => { it.value = v; save(false); }, 'flex-1'),
        input(it.sub, 'Nota (opcional)', (v) => { it.sub = v; save(false); }, 'flex-1'),
        iconBtn('✕', () => { b.items = b.items.filter((x) => x !== it); save(false); }, 'Quitar'),
      ]))));
      card.appendChild(h('div', { class: 'mt-2' }, [iconBtn('➕ KPI', () => { b.items.push({ label: '', value: '', sub: '' }); save(false); })]));
    }
    return card;
  }

  draw();
}

// ============================================================
// VISTA (solo lectura) de una sección propia
// ============================================================
export function renderCustomSection(root, ctx, section) {
  if (!section) { root.innerHTML = '<p class="text-ink-300">Sección no encontrada.</p>'; return; }
  const s = normSection(clone(section));
  const liveCfg = normCfg(ctx.cfg || {});
  const live = (liveCfg.customSections || []).find((x) => x.id === section.id) || section; // para persistir checkboxes
  const state = { tab: s.tabs[0].id };

  function draw() {
    root.innerHTML = '';
    const tab = s.tabs.find((t) => t.id === state.tab) || s.tabs[0];

    const head = h('div', { class: 'flex items-center justify-between gap-3 mb-6 flex-wrap' }, [
      h('h2', { class: 'font-display text-3xl font-bold' }, [h('span', {}, s.emoji + ' '), s.label]),
      h('button', { class: 'text-ink-300 hover:text-ink-100 text-sm px-3 py-1.5 rounded-lg bg-base-800 hover:bg-base-700 transition',
        onclick: () => { window.__openBuilderSection = s.id; ctx.navigate && ctx.navigate('constructor'); } }, '✏️ Editar'),
    ]);
    root.appendChild(head);

    if (s.tabs.length > 1) {
      root.appendChild(h('div', { class: 'flex items-center gap-1 flex-wrap mb-5 border-b border-base-700 pb-2' }, s.tabs.map((t) => h('button', {
        class: 'px-3 py-1.5 rounded-lg text-sm font-medium transition ' + (t.id === tab.id ? 'bg-gold-400/10 text-gold-300 border border-gold-400/30' : 'text-ink-200 hover:bg-base-800 border border-transparent'),
        onclick: () => { state.tab = t.id; draw(); },
      }, t.label))));
    }

    if (!tab.blocks || tab.blocks.length === 0) {
      root.appendChild(h('div', { class: 'bg-base-800 rounded-2xl p-8 text-center' }, [
        h('p', { class: 'text-ink-300' }, 'Esta sección está vacía.'),
        h('button', { class: 'mt-3 bg-gold-400 text-base-900 font-semibold px-4 py-2 rounded-lg hover:bg-gold-300 transition',
          onclick: () => { window.__openBuilderSection = s.id; ctx.navigate && ctx.navigate('constructor'); } }, '✏️ Añadir contenido'),
      ]));
      return;
    }

    const grid = h('div', { class: 'space-y-4' });
    tab.blocks.forEach((b) => grid.appendChild(viewBlock(b)));
    root.appendChild(grid);
  }

  function liveBlock(blockId) {
    // encuentra el bloque equivalente en la config viva para persistir cambios (checkbox)
    for (const t of (live.tabs || [])) for (const b of (t.blocks || [])) if (b.id === blockId) return b;
    return null;
  }

  function viewBlock(b) {
    const card = h('div', { class: 'bg-base-800 rounded-2xl p-5' });
    if (b.title) card.appendChild(h('h3', { class: 'font-display text-lg font-bold text-ink-100 mb-3' }, b.title));
    if (b.type === 'note') {
      card.appendChild(h('div', { class: 'text-ink-200 whitespace-pre-wrap leading-relaxed' }, b.text || ''));
    } else if (b.type === 'checklist') {
      const items = b.items || [];
      if (items.length === 0) card.appendChild(h('p', { class: 'text-ink-400 text-sm' }, 'Lista vacía.'));
      card.appendChild(h('div', { class: 'space-y-2' }, items.map((it) => h('label', { class: 'flex items-center gap-3 cursor-pointer' }, [
        h('input', { type: 'checkbox', checked: !!it.done, class: 'w-5 h-5 accent-blue-400',
          onchange: async (e) => {
            it.done = e.target.checked;
            const lb = liveBlock(b.id);
            if (lb && Array.isArray(lb.items)) { const m = lb.items.find((x) => x.t === it.t); if (m) m.done = it.done; }
            try { await ctx.saveCfg(clone(liveCfg)); } catch {}
          } }),
        h('span', { class: 'text-ink-100 ' + (it.done ? 'line-through text-ink-400' : '') }, it.t || ''),
      ]))));
    } else if (b.type === 'links') {
      const links = (b.links || []).filter((l) => l.url);
      card.appendChild(h('div', { class: 'flex flex-wrap gap-2' }, links.length ? links.map((l) => h('a', {
        href: l.url, target: '_blank', rel: 'noopener noreferrer',
        class: 'inline-flex items-center gap-2 bg-base-900 border border-base-700 hover:border-gold-400/50 rounded-xl px-4 py-2 text-ink-100 transition',
      }, [h('span', {}, l.emoji || '🔗'), h('span', {}, l.label || l.url)])) : [h('p', { class: 'text-ink-400 text-sm' }, 'Sin enlaces.')]));
    } else if (b.type === 'kpi') {
      const items = b.items || [];
      card.appendChild(h('div', { class: 'grid grid-cols-2 md:grid-cols-4 gap-3' }, items.map((it) => h('div', { class: 'bg-base-900 border border-base-700 rounded-xl p-4' }, [
        h('div', { class: 'text-2xl font-bold text-gold-300' }, it.value || '—'),
        h('div', { class: 'text-xs text-ink-300 mt-1' }, it.label || ''),
        it.sub ? h('div', { class: 'text-[11px] text-ink-400 mt-0.5' }, it.sub) : null,
      ]))));
    }
    return card;
  }

  draw();
}
